import React, { useState, useEffect, useRef } from 'react';
import apiService from '../apiService';

/**
 * Onglet « Applications » — publication des APK Android (distribution directe
 * via mbiyo.app/telecharger) et gestion des liens App Store iOS.
 *
 * L'APK est envoyé DIRECTEMENT du navigateur vers GCS via une URL signée
 * (il dépasse la limite de 100 Mo de Cloudflare devant l'API) ; le backend
 * n'enregistre que les métadonnées une fois l'upload terminé.
 */

const APPS = [
  { key: 'client',   label: 'Mbiyo (client)',    icon: '🚕' },
  { key: 'driver',   label: 'Mbiyo Chauffeur',   icon: '🛞' },
  { key: 'supplier', label: 'Mbiyo Business',     icon: '🏪' },
];

const box = { background: '#fff', border: '1px solid #E9EBEF', borderRadius: 12, padding: 18, marginBottom: 16 };
const input = { width: '100%', padding: '9px 11px', border: '1px solid #DCE0E6', borderRadius: 8, fontSize: 14, marginTop: 4 };
const label = { fontSize: 12.5, fontWeight: 600, color: '#5C636E' };
const btn = { padding: '10px 16px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 14, cursor: 'pointer' };

const fmtSize = (b) => (b ? (b / (1024 * 1024)).toFixed(1) + ' Mo' : '—');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// ─── Lecture de versionCode / versionName DANS l'APK choisi ─────────────────
// Un versionCode saisi à la main et différent de celui embarqué fait afficher
// « Mise à jour disponible » à des installations déjà à jour (incident du
// 2026-09-25). On lit donc le manifeste binaire de l'APK (ZIP) côté navigateur.

function parseAxmlVersions(xml) {
  const dv = new DataView(xml.buffer, xml.byteOffset, xml.byteLength);
  // String pool (chunk 0x0001) juste après l'en-tête XML (8 octets)
  let off = 8;
  if (dv.getUint16(off, true) !== 0x0001) throw new Error('AXML inattendu');
  const poolSize = dv.getUint32(off + 4, true);
  const count = dv.getUint32(off + 8, true);
  const flags = dv.getUint32(off + 16, true);
  const strOff = dv.getUint32(off + 20, true);
  const isUtf8 = !!(flags & 0x100);
  const base = off + strOff;
  const strings = [];
  for (let i = 0; i < count; i++) {
    let p = base + dv.getUint32(off + 28 + 4 * i, true);
    if (isUtf8) {
      let n = xml[p + 1]; p += 2;
      if (n & 0x80) { n = ((n & 0x7f) << 8) | xml[p]; p += 1; }
      strings.push(new TextDecoder('utf-8').decode(xml.subarray(p, p + n)));
    } else {
      let n = dv.getUint16(p, true); p += 2;
      if (n & 0x8000) { n = ((n & 0x7fff) << 16) | dv.getUint16(p, true); p += 2; }
      strings.push(new TextDecoder('utf-16le').decode(xml.subarray(p, p + 2 * n)));
    }
  }
  // Chunks suivants → START_TAG (0x0102) du <manifest>
  off += poolSize;
  while (off < xml.byteLength) {
    const type = dv.getUint16(off, true);
    const size = dv.getUint32(off + 4, true);
    if (type === 0x0102 && strings[dv.getUint32(off + 20, true)] === 'manifest') {
      const attrCount = dv.getUint16(off + 28, true);
      let p = off + 36;
      const out = {};
      for (let i = 0; i < attrCount; i++) {
        const name = strings[dv.getUint32(p + 4, true)];
        const raw = dv.getUint32(p + 8, true);
        const data = dv.getUint32(p + 16, true);
        if (name === 'versionCode') out.versionCode = data;
        if (name === 'versionName') out.versionName = raw !== 0xFFFFFFFF ? strings[raw] : String(data);
        p += 20;
      }
      return out;
    }
    if (!size) break;
    off += size;
  }
  throw new Error('balise manifest introuvable');
}

async function extractApkInfo(file) {
  const buf = new Uint8Array(await file.arrayBuffer());
  const dv = new DataView(buf.buffer);
  // End Of Central Directory (0x06054b50) dans les 64 Ko finaux
  let eocd = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65558); i--) {
    if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; }
  }
  if (eocd < 0) throw new Error('APK/ZIP invalide');
  const count = dv.getUint16(eocd + 10, true);
  let off = dv.getUint32(eocd + 16, true);
  let entry = null;
  for (let i = 0; i < count; i++) {
    if (dv.getUint32(off, true) !== 0x02014b50) break;
    const method = dv.getUint16(off + 10, true);
    const csize = dv.getUint32(off + 20, true);
    const nlen = dv.getUint16(off + 28, true);
    const elen = dv.getUint16(off + 30, true);
    const clen = dv.getUint16(off + 32, true);
    const lho = dv.getUint32(off + 42, true);
    const name = new TextDecoder().decode(buf.subarray(off + 46, off + 46 + nlen));
    if (name === 'AndroidManifest.xml') { entry = { method, csize, lho }; break; }
    off += 46 + nlen + elen + clen;
  }
  if (!entry) throw new Error('AndroidManifest.xml introuvable');
  const lnlen = dv.getUint16(entry.lho + 26, true);
  const lelen = dv.getUint16(entry.lho + 28, true);
  const start = entry.lho + 30 + lnlen + lelen;
  const comp = buf.subarray(start, start + entry.csize);
  let xml;
  if (entry.method === 0) {
    xml = comp;
  } else {
    const stream = new Blob([comp]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
    xml = new Uint8Array(await new Response(stream).arrayBuffer());
  }
  return parseAxmlVersions(xml);
}

function AppCard({ app, latest, onPublished, onMsg }) {
  const [version, setVersion] = useState('');
  const [versionCode, setVersionCode] = useState('');
  const [changelog, setChangelog] = useState('');
  const [iosUrl, setIosUrl] = useState(latest?.ios_url || '');
  const [playUrl, setPlayUrl] = useState(latest?.play_url || '');
  const [file, setFile] = useState(null);
  const [detected, setDetected] = useState(false); // version/code lus dans l'APK
  const [progress, setProgress] = useState(null); // null = pas d'upload, 0-100 = en cours
  const fileRef = useRef(null);

  const onFileChange = async (f) => {
    setFile(f || null);
    setDetected(false);
    if (!f) return;
    try {
      const info = await extractApkInfo(f);
      if (info.versionCode) {
        setVersionCode(String(info.versionCode));
        if (info.versionName) setVersion(String(info.versionName));
        setDetected(true);
        onMsg('success', `Version détectée dans l'APK : v${info.versionName || '?'} (code ${info.versionCode})`);
      }
    } catch (e) {
      onMsg('error', `Lecture de l'APK impossible (${e.message}) — saisissez la version manuellement, EXACTEMENT celle du build.`);
    }
  };

  useEffect(() => { setIosUrl(latest?.ios_url || ''); }, [latest?.ios_url]);
  useEffect(() => { setPlayUrl(latest?.play_url || ''); }, [latest?.play_url]);

  const publish = async () => {
    const vc = parseInt(versionCode, 10);
    if (!file) return onMsg('error', 'Choisissez le fichier APK.');
    if (!file.name.endsWith('.apk')) return onMsg('error', 'Le fichier doit être un .apk (pas un .aab — Google Play uniquement).');
    if (!version.trim() || !vc || vc <= 0) return onMsg('error', 'Version (ex. 1.5.31) et versionCode (entier) requis.');

    try {
      // 1) URL signée d'upload
      setProgress(0);
      const r1 = await apiService.post('/app-releases/upload-url',
        { app: app.key, version: version.trim(), version_code: vc }, { userType: 'admin' });
      if (!r1.success) throw new Error(r1.error || 'upload-url');

      // 2) PUT direct navigateur → GCS avec progression (PAS de header Authorization :
      //    l'URL signée est l'autorisation ; le Content-Type doit être EXACTEMENT celui signé).
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', r1.upload_url);
        xhr.setRequestHeader('Content-Type', r1.content_type);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`GCS ${xhr.status}`)));
        xhr.onerror = () => reject(new Error('Échec réseau vers GCS (CORS du bucket configuré ? scripts/set-gcs-cors.js)'));
        xhr.send(file);
      });

      // 3) Enregistrer les métadonnées
      const r2 = await apiService.post('/app-releases', {
        app: app.key, version: version.trim(), version_code: vc,
        gcs_path: r1.gcs_path, size_bytes: file.size, changelog: changelog.trim(),
      }, { userType: 'admin' });
      if (!r2.success) throw new Error(r2.error || 'enregistrement');

      onMsg('success', `${app.label} v${version} publiée ✔ (en ligne sur /telecharger)`);
      setVersion(''); setVersionCode(''); setChangelog(''); setFile(null); setDetected(false);
      if (fileRef.current) fileRef.current.value = '';
      onPublished();
    } catch (e) {
      onMsg('error', `Publication échouée : ${e.message}`);
    } finally { setProgress(null); }
  };

  const saveIosUrl = async () => {
    try {
      const r = await apiService.patch('/app-releases/store-url', { app: app.key, url: iosUrl.trim() }, { userType: 'admin' });
      if (!r.success) throw new Error(r.error || '');
      onMsg('success', `Lien App Store de ${app.label} mis à jour ✔`);
    } catch (e) { onMsg('error', `Lien App Store non enregistré : ${e.message}`); }
  };

  const savePlayUrl = async () => {
    try {
      const r = await apiService.patch('/app-releases/store-url', { app: app.key, play_url: playUrl.trim() }, { userType: 'admin' });
      if (!r.success) throw new Error(r.error || '');
      onMsg('success', `Lien Google Play de ${app.label} mis à jour ✔ (sur mbiyo.app/telecharger, le store devient le bouton principal ; l'APK reste en secours)`);
    } catch (e) { onMsg('error', `Lien Google Play non enregistré : ${e.message}`); }
  };

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 26 }}>{app.icon}</span>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{app.label}</div>
          <div style={{ fontSize: 12.5, color: '#5C636E' }}>
            {latest?.version
              ? <>En ligne : <b>v{latest.version}</b> (code {latest.version_code}) · {fmtSize(latest.size_bytes)} · {fmtDate(latest.published_at)}</>
              : 'Aucun APK publié pour le moment'}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div><span style={label}>Version (affichée) {detected && '🔒'}</span>
          <input style={input} placeholder="1.6.0" value={version} readOnly={detected}
            onChange={e => setVersion(e.target.value)} /></div>
        <div><span style={label}>versionCode Android {detected && '🔒'}</span>
          <input style={input} type="number" placeholder="21" value={versionCode} readOnly={detected}
            onChange={e => setVersionCode(e.target.value)} /></div>
      </div>
      {detected && (
        <div style={{ fontSize: 12, color: '#166534', marginTop: 4 }}>
          ✔ Lus directement dans l'APK — non modifiables (c'est ce que compare le bandeau de mise à jour dans l'app).
        </div>
      )}
      <div style={{ marginTop: 10 }}><span style={label}>Nouveautés (affichées sur la page de téléchargement)</span>
        <textarea style={{ ...input, minHeight: 60, resize: 'vertical' }} value={changelog} onChange={e => setChangelog(e.target.value)}
          placeholder={'- Correction du plantage des cartes\n- Recherche de lieu améliorée'} /></div>
      <div style={{ marginTop: 10 }}><span style={label}>Fichier APK</span>
        <input ref={fileRef} style={{ ...input, padding: 8 }} type="file" accept=".apk"
          onChange={e => onFileChange(e.target.files?.[0])} />
        {file && <div style={{ fontSize: 12, color: '#5C636E', marginTop: 3 }}>{file.name} — {fmtSize(file.size)}</div>}
      </div>

      {progress !== null && (
        <div style={{ marginTop: 12 }}>
          <div style={{ height: 10, background: '#E9EBEF', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: '#10B981', transition: 'width .2s' }} />
          </div>
          <div style={{ fontSize: 12, color: '#5C636E', marginTop: 4 }}>
            {progress < 100 ? `Envoi vers le stockage… ${progress} %` : 'Enregistrement…'}
          </div>
        </div>
      )}

      <button style={{ ...btn, background: '#10B981', color: '#fff', marginTop: 12, opacity: progress !== null ? 0.6 : 1 }}
        disabled={progress !== null} onClick={publish}>
        {progress !== null ? 'Publication en cours…' : '📦 Publier cette version'}
      </button>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #E9EBEF' }}>
        <span style={label}> Lien App Store (iOS)</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={input} value={iosUrl} onChange={e => setIosUrl(e.target.value)} placeholder="https://apps.apple.com/…" />
          <button style={{ ...btn, background: '#12151A', color: '#fff', marginTop: 4 }} onClick={saveIosUrl}>Enregistrer</button>
        </div>
        <span style={{ ...label, marginTop: 10 }}>▶ Lien Google Play (Android)</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={input} value={playUrl} onChange={e => setPlayUrl(e.target.value)} placeholder="https://play.google.com/store/apps/details?id=…" />
          <button style={{ ...btn, background: '#16a34a', color: '#fff', marginTop: 4 }} onClick={savePlayUrl}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}

export default function AppReleasesPage() {
  const [releases, setReleases] = useState([]);
  const [history, setHistory] = useState([]);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const load = async () => {
    try {
      const r = await apiService.get('/app-releases', { userType: 'admin' });
      if (r.success) setReleases(r.releases);
      const h = await apiService.get('/app-releases/history', { userType: 'admin' });
      if (h.success) setHistory(h.releases);
    } catch { setMsg({ type: 'error', text: 'Erreur de chargement' }); }
  };
  useEffect(() => { load(); }, []);

  const onMsg = (type, text) => {
    setMsg({ type, text });
    if (type === 'success') setTimeout(() => setMsg({ type: '', text: '' }), 6000);
  };

  const retire = async (rel) => {
    if (!window.confirm(`Retirer ${rel.app} v${rel.version} ? La version précédente redeviendra celle proposée au téléchargement.`)) return;
    try {
      const r = await apiService.delete(`/app-releases/${rel.release_id}`, { userType: 'admin' });
      if (!r.success) throw new Error(r.error || '');
      onMsg('success', `v${rel.version} retirée`);
      load();
    } catch (e) { onMsg('error', `Échec : ${e.message}`); }
  };

  return (
    <div style={{ maxWidth: 860 }}>
      <h2 style={{ marginBottom: 4 }}>📱 Applications</h2>
      <p style={{ fontSize: 13.5, color: '#5C636E', marginBottom: 16 }}>
        Publication des APK Android proposés sur <a href="https://mbiyo.app/telecharger" target="_blank" rel="noreferrer">mbiyo.app/telecharger</a> et
        liens App Store. Le bandeau de mise à jour dans les apps se base sur le <b>versionCode</b> : il doit être
        strictement supérieur au précédent. ⚠️ Fichier <b>.apk</b> (profil EAS « apk »), pas .aab.
      </p>

      {msg.text && (
        <div style={{
          padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 14,
          background: msg.type === 'error' ? '#FEF2F2' : '#ECFDF5',
          color: msg.type === 'error' ? '#991B1B' : '#166534',
          border: `1px solid ${msg.type === 'error' ? '#FECACA' : '#BBF7D0'}`,
        }}>{msg.text}</div>
      )}

      {APPS.map(app => (
        <AppCard key={app.key} app={app}
          latest={releases.find(r => r.app === app.key)}
          onPublished={load} onMsg={onMsg} />
      ))}

      {history.length > 0 && (
        <div style={box}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Historique</div>
          <table style={{ width: '100%', fontSize: 13.5, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#5C636E' }}>
                <th style={{ padding: '6px 4px' }}>App</th><th>Version</th><th>Code</th><th>Taille</th><th>Date</th><th>Statut</th><th></th>
              </tr>
            </thead>
            <tbody>
              {history.map(rel => (
                <tr key={rel.release_id} style={{ borderTop: '1px solid #F4F5F7' }}>
                  <td style={{ padding: '7px 4px' }}>{APPS.find(a => a.key === rel.app)?.label || rel.app}</td>
                  <td>v{rel.version}</td>
                  <td>{rel.version_code}</td>
                  <td>{fmtSize(rel.size_bytes)}</td>
                  <td>{fmtDate(rel.created_at)}</td>
                  <td>{rel.active ? '🟢 active' : '⚪ retirée'}</td>
                  <td>{rel.active && (
                    <button style={{ ...btn, padding: '4px 10px', fontSize: 12.5, background: '#FEF2F2', color: '#991B1B' }}
                      onClick={() => retire(rel)}>Retirer</button>
                  )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
